import { useState, type ReactNode } from 'react'
import { Building2, Check, ArrowRight, ArrowLeft, RotateCcw, Code2, ChevronDown, ChevronUp } from 'lucide-react'
import { inputCls, labelCls } from '../styles'
import { StageGuidedShell } from './StageGuidedShell'
import type { GuidedStep } from './StageGuidedShell'
import { EnvironmentSelector } from './EnvironmentSelector'
import type { EnvAccounts, EnvId } from '../types'

interface AccountSetupGuidedViewProps {
  customerName: string
  onCustomerNameChange: (name: string) => void
  version: 'v1' | 'v2'
  onVersionChange: (v: 'v1' | 'v2') => void
  selectedEnvs: EnvId[]
  onSelectedEnvsChange: (envs: EnvId[]) => void
  provisioning: boolean
  currentStep: string | null
  canProvision: boolean
  onProvision: () => void
  envAccounts: EnvAccounts[]
  technicalDetails: ReactNode
}

export function AccountSetupGuidedView({
  customerName, onCustomerNameChange, version, onVersionChange, selectedEnvs, onSelectedEnvsChange, provisioning, currentStep,
  canProvision, onProvision, envAccounts, technicalDetails,
}: AccountSetupGuidedViewProps) {
  const [showTechnical, setShowTechnical] = useState(false)

  const steps: GuidedStep[] = [
    {
      label: 'Overview',
      render: ({ next }) => (
        <div className="rounded-[18px] p-8 text-center" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,.12), rgba(167,139,250,.06))', border: '1px solid rgba(99,102,241,.25)' }}>
          <div className="w-16 h-16 rounded-[18px] flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(99,102,241,.18)' }}>
            <Building2 size={30} className="text-[#4f46e5]" />
          </div>
          <h2 className="text-[22px] font-bold text-[#1e293b]">Set Up This Customer</h2>
          <p className="text-[14px] text-[#4f46e5] mt-2 max-w-md mx-auto leading-relaxed">
            Before anything else runs, this customer needs an account shell — created across every environment
            in seconds, ready for the rest of the demo.
          </p>
          <button
            onClick={next}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-[10px] text-[14px] font-semibold text-white transition-all"
            style={{ background: '#6366f1', boxShadow: '0 4px 16px rgba(99,102,241,.5)' }}
          >
            Get started <ArrowRight size={15} />
          </button>
        </div>
      ),
    },
    {
      label: 'Customer details',
      render: ({ next, back }) => (
        <div className="space-y-4">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1e293b]">Tell us about this customer</h3>
            <p className="text-[12px] text-[#475569] mt-1">Just the essentials — everything else uses sensible defaults.</p>
          </div>
          <div className="rounded-[14px] p-5 space-y-3" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
            <div>
              <label className={labelCls}>Customer name</label>
              <input
                className={inputCls}
                value={customerName}
                onChange={(e) => onCustomerNameChange(e.target.value)}
                placeholder="e.g. Acme Corp"
                disabled={provisioning}
              />
            </div>
            <div>
              <label className={labelCls}>Platform version</label>
              <div className="flex rounded-[8px] overflow-hidden border border-[#e5e7eb]">
                {(['v2', 'v1'] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => onVersionChange(v)}
                    disabled={provisioning}
                    className="flex-1 py-2 text-[12px] font-semibold uppercase transition-colors"
                    style={version === v ? { background: '#4f46e5', color: '#ffffff' } : { background: '#f8fafc', color: '#64748b' }}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <EnvironmentSelector selected={selectedEnvs} onChange={onSelectedEnvsChange} disabled={provisioning} />
          </div>
          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={next} disabled={!customerName.trim() || selectedEnvs.length === 0} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-[9px] text-[13px] font-semibold text-white disabled:opacity-40" style={{ background: '#6366f1' }}>
              Next <ArrowRight size={13} />
            </button>
          </div>
        </div>
      ),
    },
    {
      label: 'Set up',
      render: ({ next, back }) => (
        <div className="space-y-4">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1e293b]">Ready to set up {customerName || 'this customer'}</h3>
            <p className="text-[12px] text-[#475569] mt-1">
              {selectedEnvs.length > 0
                ? `Runs across ${selectedEnvs.map((e) => e.toUpperCase()).join(', ')} in sequence.`
                : 'Select at least one environment to continue.'}
            </p>
          </div>
          <button
            onClick={onProvision}
            disabled={!canProvision}
            className="w-full py-3 rounded-[10px] text-[14px] font-semibold text-white transition-all disabled:opacity-40"
            style={{ background: '#6366f1', boxShadow: '0 4px 16px rgba(99,102,241,.5)' }}
          >
            {provisioning ? (currentStep ?? 'Setting up…') : 'Set up this customer'}
          </button>
          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={next} disabled={envAccounts.length === 0} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-[9px] text-[13px] font-semibold text-white disabled:opacity-40" style={{ background: '#6366f1' }}>
              See result <ArrowRight size={13} />
            </button>
          </div>
        </div>
      ),
    },
    {
      label: 'Result',
      render: ({ back, restart }) => (
        <div className="space-y-4">
          <div className="rounded-[18px] p-6 flex items-center gap-4" style={{ background: envAccounts.length > 0 ? 'rgba(52,211,153,.06)' : '#ffffff', border: `1px solid ${envAccounts.length > 0 ? 'rgba(52,211,153,.25)' : '#e5e7eb'}` }}>
            <div className="w-12 h-12 rounded-[12px] flex items-center justify-center shrink-0" style={{ background: envAccounts.length > 0 ? 'rgba(52,211,153,.15)' : 'rgba(99,102,241,.12)' }}>
              {envAccounts.length > 0 ? <Check size={24} className="text-emerald-700" /> : <Building2 size={24} className="text-[#6366f1]" />}
            </div>
            <div>
              <p className="text-[15px] font-semibold text-[#1e293b]">
                {envAccounts.length > 0 ? `${customerName}'s account is ready.` : 'Waiting for setup to finish…'}
              </p>
              {envAccounts.length > 0 && (
                <div className="flex gap-1.5 mt-1.5">
                  {envAccounts.map((ea) => (
                    <span key={ea.env} className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ background: 'rgba(52,211,153,.15)', color: '#15803d' }}>{ea.env}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
            <button
              onClick={() => setShowTechnical((s) => !s)}
              className="w-full flex items-center justify-between px-4 py-3 transition-colors hover:bg-[#ffffff]"
              style={{ background: '#f8fafc' }}
            >
              <span className="flex items-center gap-2 text-[12px] font-semibold text-[#475569]">
                <Code2 size={13} /> Advanced options & technical details
              </span>
              {showTechnical ? <ChevronUp size={13} className="text-[#64748b]" /> : <ChevronDown size={13} className="text-[#64748b]" />}
            </button>
            {showTechnical && <div className="p-4 space-y-3" style={{ background: '#ffffff' }}>{technicalDetails}</div>}
          </div>

          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={restart} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <RotateCcw size={13} /> Start over
            </button>
          </div>
        </div>
      ),
    },
  ]

  return <StageGuidedShell steps={steps} />
}
